/* eslint-disable deprecation/deprecation */
import { Firestore } from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from '@angular/fire/storage';
import { Database, ref as dbRef, get, update } from '@angular/fire/database';
import { captureException } from '@sentry/angular';
import { MachineModel } from '../models/machine.model';
import { environment } from '../../environments/environment';
import { Progress } from './migration-types';

const contentTypeToExtension = (contentType: string) => {
  switch (contentType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'image/webp':
      return 'webp';
    case 'image/avif':
      return 'avif';
    default:
      console.error('Unknown content type:', contentType);
      return 'jpg';
  }
};

const getCorrectUrl = (originalUrl: string) => {
  // covers base64 images
  if (!originalUrl.startsWith('http')) {
    return originalUrl;
  }

  const url = new URL(originalUrl);

  if (url.hostname !== 'storage.googleapis.com') {
    return originalUrl;
  }

  const path = url.pathname.replace(
    new RegExp(`/${environment.firebase.storageBucket}/`),
    '',
  );

  return `http://${environment.firebase.storageBucket}.storage.googleapis.com/${path}`;
};

/**
 * Phase 1 Migration: Image Migration
 *
 * Converts deprecated single 'image' field to 'images' map in machine documents.
 * This migration runs BEFORE Phase 3 (Firestore migration), so it operates entirely on RTDB.
 *
 * IMPORTANT: This migration reads from and writes to RTDB, NOT Firestore.
 * Phase 3 will later migrate all RTDB data (including the new images field) to Firestore.
 *
 * Flow:
 * 1. Read machines from RTDB
 * 2. For each machine with an 'image' field:
 *    - Upload image to Storage
 *    - Delete old image from Storage (if not base64)
 *    - Write machine back to RTDB with new images field (as document field, not subcollection)
 *    - Clear old image field in RTDB
 */
export async function* migrateImages(
  uid: string,
  db: Database,
  firestore: Firestore,
  storage: Storage,
): AsyncGenerator<Progress, void, unknown> {
  // Read machines from RTDB (pre-Firestore migration)
  const snapshot = await get(dbRef(db, `/userProfile/${uid}/machines`));
  const data = snapshot.val();

  if (!data) {
    yield { type: 'done' };
    return;
  }

  // Convert object to array with id as key
  const machines = Object.entries(data).map(([id, machineData]) =>
    new MachineModel({ ...(machineData as object), id })
  );

  const notMigratedMachines = machines.filter(
    (machine: MachineModel) => machine.image,
  );

  if (notMigratedMachines.length === 0) {
    yield { type: 'done' };
    return;
  }

  for (const [index, machine] of notMigratedMachines.entries()) {
    yield {
      type: 'progress',
      current: index,
      total: notMigratedMachines.length,
    };

    if (!machine.image) {
      continue;
    }

    try {
      const url = getCorrectUrl(machine.image);
      const isBase64 = !machine.image.startsWith('http');
      const response = await fetch(url);
      const blob = await response.blob();
      const extension = contentTypeToExtension(blob.type);
      const fileName = 'image' + '.' + extension;
      const file = new File([blob], fileName, {
        type: blob.type,
      });

      // Upload image to Storage and write metadata directly to Firestore
      const randomId = Math.random().toString(36).substring(2);
      const storageFileName = `${randomId}.${extension}`;
      const filePath = `${uid}/${machine.id}/${storageFileName}`;
      const imageRef = ref(storage, filePath);
      await uploadBytes(imageRef, file);
      const downloadUrl = await getDownloadURL(imageRef);

      const imageData = {
        url: downloadUrl,
        ref: imageRef.toString(),
      };

      // Delete old image if it's not base64
      if (!isBase64) {
        try {
          await deleteObject(ref(storage, machine.image));
        } catch {
          console.error('Error deleting image', machine.image);
        }
      }

      // IMPORTANT: This migration runs BEFORE Firestore migration (Phase 3).
      // We write back to RTDB, and later Phase 3 will migrate everything to Firestore.
      // Write machine to RTDB with new images field and clear old image field
      await update(dbRef(db, `/userProfile/${uid}/machines/${machine.id}`), {
        image: null,
        images: {
          [randomId]: imageData,
        },
      });
    } catch (error) {
      captureException(error, {
        extra: {
          machineId: machine.id,
        },
      });
    }
  }

  yield { type: 'done' };
}
